import React from 'react';
import './App.css';
import UniversalHeader from './UniversalHeader';
import { useNavigate } from 'react-router-dom';
import { supabase } from './supabaseClient';

function formatLeaseDate(value) {
  if (!value) return '—';
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleDateString(undefined, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return '—';
  }
}

function propertyDisplayName(row) {
  const rel = row.properties;
  const prop = Array.isArray(rel) ? rel[0] : rel;
  if (!prop?.name) return 'No property assigned';
  return prop.location ? `${prop.name} (${prop.location})` : prop.name;
}

function mapRowToTenant(row) {
  return {
    id: row.id,
    name: row.full_name || '—',
    email: row.email || '—',
    phone: row.phone || '—',
    property: propertyDisplayName(row),
    room: row.room_label || '—',
    start: formatLeaseDate(row.move_in_date),
    end: formatLeaseDate(row.move_out_date),
    rentStatus: row.rent_status || 'pending',
  };
}

function Tenants() {
  const [search, setSearch] = React.useState('');
  const [filter, setFilter] = React.useState('all');
  const [tenants, setTenants] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [listError, setListError] = React.useState('');
  const navigate = useNavigate();

  const [detailId, setDetailId] = React.useState(null);
  const [detailLoading, setDetailLoading] = React.useState(false);
  const [detailError, setDetailError] = React.useState('');
  const [detailData, setDetailData] = React.useState(null);

  const [deleteTarget, setDeleteTarget] = React.useState(null);
  const [deleteBusy, setDeleteBusy] = React.useState(false);

  const [toast, setToast] = React.useState(null);

  React.useEffect(() => {
    if (!toast) return undefined;
    const t = window.setTimeout(() => setToast(null), 3500);
    return () => window.clearTimeout(t);
  }, [toast]);

  const loadTenants = React.useCallback(async () => {
    if (!supabase) {
      setListError('Supabase is not configured.');
      setTenants([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setListError('');
    const { data, error } = await supabase
      .from('prospective_tenants')
      .select(
        `
        id,
        full_name,
        email,
        phone,
        rent_status,
        move_in_date,
        move_out_date,
        room_label,
        properties (
          name,
          location
        )
      `
      )
      .order('created_at', { ascending: false });

    if (error) {
      setListError(error.message);
      setTenants([]);
    } else {
      setTenants((data || []).map(mapRowToTenant));
    }
    setLoading(false);
  }, []);

  React.useEffect(() => {
    loadTenants();
  }, [loadTenants]);

  React.useEffect(() => {
    if (!detailId) return undefined;

    if (!supabase) {
      setDetailLoading(false);
      setDetailError('Supabase is not configured.');
      setDetailData(null);
      return undefined;
    }

    let cancelled = false;
    setDetailLoading(true);
    setDetailError('');
    setDetailData(null);

    (async () => {
      const { data, error } = await supabase
        .from('prospective_tenants')
        .select(
          `
          id,
          full_name,
          email,
          phone,
          rent_status,
          move_in_date,
          move_out_date,
          room_label,
          created_at,
          properties (
            name,
            location
          )
        `
        )
        .eq('id', detailId)
        .single();

      if (cancelled) return;

      if (error) {
        setDetailError(error.message);
      } else {
        setDetailData(data);
      }
      setDetailLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [detailId]);

  React.useEffect(() => {
    const modalOpen = Boolean(detailId || deleteTarget);
    if (!modalOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        if (deleteTarget) setDeleteTarget(null);
        else {
          setDetailId(null);
          setDetailData(null);
          setDetailError('');
        }
      }
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [detailId, deleteTarget]);

  const closeDetailModal = () => {
    setDetailId(null);
    setDetailData(null);
    setDetailError('');
  };

  const openDetailModal = (tenant) => {
    if (!supabase) {
      // eslint-disable-next-line no-alert
      alert('Supabase is not configured.');
      return;
    }
    setDeleteTarget(null);
    setDetailId(tenant.id);
  };

  const openDeleteModal = (tenant) => {
    setDetailId(null);
    setDetailData(null);
    setDetailError('');
    setDeleteTarget(tenant);
  };

  const closeDeleteModal = () => {
    setDeleteTarget(null);
  };

  const confirmDelete = async () => {
    if (!deleteTarget || !supabase) return;
    setDeleteBusy(true);
    try {
      const { error } = await supabase
        .from('prospective_tenants')
        .delete()
        .eq('id', deleteTarget.id);
      if (error) throw error;
      setDeleteTarget(null);
      setToast(`“${deleteTarget.name}” was removed.`);
      await loadTenants();
    } catch (err) {
      // eslint-disable-next-line no-alert
      alert(err?.message || 'Could not delete tenant.');
    } finally {
      setDeleteBusy(false);
    }
  };

  const filtered = tenants.filter((t) => {
    const term = search.trim().toLowerCase();
    const matchesSearch =
      !term ||
      t.name.toLowerCase().includes(term) ||
      t.email.toLowerCase().includes(term) ||
      t.property.toLowerCase().includes(term) ||
      t.room.toLowerCase().includes(term);
    const matchesFilter =
      filter === 'all' ||
      (filter === 'paid' && t.rentStatus === 'paid') ||
      (filter === 'pending' && t.rentStatus === 'pending') ||
      (filter === 'overdue' && t.rentStatus === 'overdue');
    return matchesSearch && matchesFilter;
  });

  const rentLabel = {
    paid: 'Paid',
    pending: 'Pending',
    overdue: 'Overdue',
  };

  const handleAdd = () => {
    navigate('/tenants/new');
  };

  const handleEdit = (tenant) => {
    navigate(`/tenants/${tenant.id}/edit`);
  };

  const initials = (name) => {
    const parts = (name || '').trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return '?';
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return `${parts[0][0] || ''}${parts[parts.length - 1][0] || ''}`.toUpperCase();
  };

  return (
    <div className="tenants-root">
      <UniversalHeader />

      {toast ? (
        <div className="tenants-toast" role="status">
          {toast}
        </div>
      ) : null}

      <div className="tenants-shell">
        <header className="tenants-header">
          <div>
            <h1 className="tenants-title">Prospective tenants</h1>
            <p className="tenants-subtitle">
              Track leads, contact details, and rent status in one place.
            </p>
          </div>

          <div className="tenants-actions">
            <button type="button" className="btn-ghost tenants-btn">
              Export
            </button>
            <button type="button" className="btn-primary tenants-btn" onClick={handleAdd}>
              + Add prospective tenant
            </button>
          </div>
        </header>

        {listError ? (
          <p className="properties-save-error" role="alert">
            {listError}
          </p>
        ) : null}

        <section className="tenants-toolbar">
          <div className="tenants-search">
            <div className="input-shell tenants-search-shell">
              <div className="input-icon">🔍</div>
              <input
                className="input-element"
                type="text"
                placeholder="Search by name, email, or property"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>

          <div className="tenants-filters">
            {['all', 'paid', 'pending', 'overdue'].map((key) => (
              <button
                key={key}
                type="button"
                className={`tenants-filter-chip ${
                  filter === key ? 'tenants-filter-chip--active' : ''
                }`}
                onClick={() => setFilter(key)}
              >
                {key === 'all' ? 'All' : rentLabel[key]}
              </button>
            ))}
          </div>
        </section>

        <main className="tenants-layout">
          <section className="tenants-list">
            <div className="tenants-list-header">
              <span className="tenants-list-label">Prospective tenants</span>
              <span className="tenants-list-count">
                {loading ? '…' : `${filtered.length} total`}
              </span>
            </div>

            {loading ? (
              <p className="properties-list-empty">Loading prospective tenants…</p>
            ) : (
              <div className="tenants-grid">
                {filtered.length === 0 ? (
                  <p className="properties-list-empty">
                    No prospective tenants yet. Use “+ Add prospective tenant” above.
                  </p>
                ) : (
                  filtered.map((t) => (
                    <article className="tenants-card" key={t.id}>
                      <div className="tenants-card-main">
                        <div className="tenants-avatar">
                          <span>{initials(t.name)}</span>
                        </div>
                        <div className="tenants-main-text">
                          <h2 className="tenants-card-name">{t.name}</h2>
                          <p className="tenants-card-contact">
                            {t.email} · {t.phone}
                          </p>
                          <p className="tenants-card-property">
                            {t.property} · Room {t.room}
                          </p>
                        </div>
                        <span
                          className={`tenants-rent-badge tenants-rent-badge--${t.rentStatus}`}
                        >
                          {rentLabel[t.rentStatus]}
                        </span>
                      </div>

                      <div className="tenants-card-meta">
                        <div className="tenants-meta-block">
                          <span className="tenants-meta-label">Lease</span>
                          <span className="tenants-meta-value">
                            {t.start} – {t.end}
                          </span>
                        </div>
                        <button type="button" className="tenants-doc-link">
                          Lease documents
                        </button>
                      </div>

                      <div className="tenants-card-actions">
                        <button
                          type="button"
                          className="properties-icon-btn"
                          aria-label="View tenant details"
                          onClick={() => openDetailModal(t)}
                        >
                          👁
                        </button>
                        <button
                          type="button"
                          className="properties-icon-btn"
                          aria-label="Open lease documents"
                          onClick={() => {
                            // eslint-disable-next-line no-alert
                            alert(`Open documents for ${t.name}`);
                          }}
                        >
                          📄
                        </button>
                        <button
                          type="button"
                          className="properties-icon-btn"
                          aria-label="Edit tenant"
                          onClick={() => handleEdit(t)}
                        >
                          ✏️
                        </button>
                        <button
                          type="button"
                          className="properties-icon-btn properties-icon-btn--danger"
                          aria-label="Delete tenant"
                          onClick={() => openDeleteModal(t)}
                        >
                          🗑
                        </button>
                      </div>
                    </article>
                  ))
                )}
              </div>
            )}
          </section>
        </main>
      </div>

      {detailId ? (
        <div
          className="properties-modal-root"
          role="presentation"
          onClick={closeDetailModal}
        >
          <div
            className="properties-modal-panel"
            role="dialog"
            aria-modal="true"
            aria-labelledby="tenant-detail-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="properties-modal-header">
              <h2 id="tenant-detail-title" className="properties-modal-title">
                Tenant details
              </h2>
              <button
                type="button"
                className="properties-modal-close"
                aria-label="Close"
                onClick={closeDetailModal}
              >
                ×
              </button>
            </div>

            <div className="properties-modal-body">
              {detailLoading ? (
                <p className="properties-list-empty">Loading…</p>
              ) : null}
              {detailError ? (
                <p className="properties-save-error" role="alert">
                  {detailError}
                </p>
              ) : null}
              {!detailLoading && detailData ? (
                <>
                  <div className="properties-modal-section">
                    <h3 className="properties-modal-label">Full name</h3>
                    <p className="properties-modal-value">
                      {detailData.full_name || '—'}
                    </p>
                  </div>
                  <div className="properties-modal-grid">
                    <div className="properties-modal-section">
                      <h3 className="properties-modal-label">Email</h3>
                      <p className="properties-modal-value">
                        {detailData.email || '—'}
                      </p>
                    </div>
                    <div className="properties-modal-section">
                      <h3 className="properties-modal-label">Phone</h3>
                      <p className="properties-modal-value">
                        {detailData.phone || '—'}
                      </p>
                    </div>
                    <div className="properties-modal-section">
                      <h3 className="properties-modal-label">Rent status</h3>
                      <p className="properties-modal-value">
                        {rentLabel[detailData.rent_status] ||
                          detailData.rent_status ||
                          '—'}
                      </p>
                    </div>
                  </div>
                  <div className="properties-modal-section">
                    <h3 className="properties-modal-label">Property</h3>
                    <p className="properties-modal-value">
                      {propertyDisplayName(detailData)}
                    </p>
                  </div>
                  <div className="properties-modal-section">
                    <h3 className="properties-modal-label">Room</h3>
                    <p className="properties-modal-value">
                      {detailData.room_label || '—'}
                    </p>
                  </div>
                  <div className="properties-modal-section">
                    <h3 className="properties-modal-label">Lease dates</h3>
                    <p className="properties-modal-value">
                      {formatLeaseDate(detailData.move_in_date)} –{' '}
                      {formatLeaseDate(detailData.move_out_date)}
                    </p>
                  </div>
                </>
              ) : null}
            </div>

            <div className="properties-modal-footer">
              <button
                type="button"
                className="btn-primary properties-btn"
                onClick={closeDetailModal}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {deleteTarget ? (
        <div
          className="tenants-delete-modal-root"
          role="presentation"
          onClick={closeDeleteModal}
        >
          <div
            className="properties-modal-panel tenants-delete-modal-panel"
            role="dialog"
            aria-modal="true"
            aria-labelledby="tenant-delete-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="properties-modal-header">
              <h2 id="tenant-delete-title" className="properties-modal-title">
                Delete tenant?
              </h2>
              <button
                type="button"
                className="properties-modal-close"
                aria-label="Close"
                onClick={closeDeleteModal}
              >
                ×
              </button>
            </div>
            <div className="properties-modal-body">
              <p className="properties-modal-value" style={{ margin: 0 }}>
                This will permanently remove{' '}
                <strong>{deleteTarget.name}</strong> from prospective tenants. This
                cannot be undone.
              </p>
            </div>
            <div className="properties-modal-footer tenants-delete-modal-footer">
              <button
                type="button"
                className="btn-ghost properties-btn"
                disabled={deleteBusy}
                onClick={closeDeleteModal}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-primary properties-btn"
                style={{ background: '#b91c1c', borderColor: '#b91c1c' }}
                disabled={deleteBusy}
                onClick={confirmDelete}
              >
                {deleteBusy ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default Tenants;
